import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api/client';
import { REFRESH_LEAD_MS } from '@/lib/config';
import {
  clearSession,
  getClaims,
  isAuthenticated,
  setTokens,
  timeUntilExpiry,
} from './session';
import { refreshSession, setRefreshFailureHandler } from '@/lib/api/refresh';
import { roleHas, type Permission } from '@/lib/rbac/permissions';
import type { AuthTokens, Role, User } from '@/lib/api/types';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  role: Role | null;
  can: (permission: Permission) => boolean;
  /** True for translator-style accounts: can translate but not write (spec §6.3). */
  isTranslateOnly: boolean;
  setSession: (tokens: AuthTokens) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Deep-link to return to after re-auth (spec §3.2). */
  pendingDeepLink: string | null;
  setPendingDeepLink: (path: string | null) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [pendingDeepLink, setPendingDeepLink] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hardLogout = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    clearSession();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Proactive refresh ~60s before expiry; reschedules itself (plan §4.2).
  const scheduleRefresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isAuthenticated()) return;
    const delay = Math.max(0, timeUntilExpiry() - REFRESH_LEAD_MS);
    timerRef.current = setTimeout(() => {
      void refreshSession()
        .then(scheduleRefresh)
        .catch(() => {
          /* failure handler hard-logs out */
        });
    }, delay);
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.get<User>('/admin/me');
    setUser(me);
  }, []);

  const setSession = useCallback(
    async (tokens: AuthTokens) => {
      setTokens(tokens);
      await refreshUser();
      setStatus('authenticated');
      scheduleRefresh();
    },
    [refreshUser, scheduleRefresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/admin/auth/logout');
    } catch {
      /* logout is best-effort; always clear locally */
    }
    hardLogout();
  }, [hardLogout]);

  // Wire the refresh-failure handler once.
  useEffect(() => {
    setRefreshFailureHandler(hardLogout);
  }, [hardLogout]);

  // Bootstrap on mount: attempt a cookie-based refresh to re-establish a session after a
  // hard reload. If it fails, we're simply unauthenticated (no error surfaced).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshSession();
        if (cancelled) return;
        await refreshUser();
        if (cancelled) return;
        setStatus('authenticated');
        scheduleRefresh();
      } catch {
        if (!cancelled) setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refreshUser, scheduleRefresh]);

  const role = user?.role ?? getClaims()?.role ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      role,
      can: (p: Permission) => roleHas(role, p),
      isTranslateOnly: roleHas(role, 'content:translate') && !roleHas(role, 'content:write'),
      setSession,
      logout,
      refreshUser,
      pendingDeepLink,
      setPendingDeepLink,
    }),
    [status, user, role, setSession, logout, refreshUser, pendingDeepLink],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
